/**
 * Sidebar — 252px, grouped nav with counts, active-item highlight.
 * Premium pass: gradient logo tile with depth, hover-reactive rows, an
 * accent left-rail on the active item, pill-style count badges, and an
 * elevated user chip with a live presence dot. All colors come from theme
 * tokens; web gets smooth transitions + gradients, native degrades gracefully.
 */
import React, { useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View, ViewStyle } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { useAuthFlow } from '../navigation/authFlowContext';
import SkIcon from '../components/skIcon';
import { NAV_GROUPS } from '../navigation/navModel';
import type { AppScreen } from '../navigation/types';

interface Props {
  active: AppScreen;
  onNavigate: (screen: AppScreen) => void;
}

const isWeb = Platform.OS === 'web';
// Phones and tablets cannot hover, so the hover card that holds "Log out" never
// opens there. Such devices get the inline button instead, as native does.
const canHover =
  isWeb && typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(hover: hover)').matches
    : false;
// web-only style escape hatch (gradients / transitions / cursor) — typed loose.
const web = (s: Record<string, unknown>): ViewStyle => (isWeb ? (s as ViewStyle) : ({} as ViewStyle));

/** Hover-aware nav row. Owns its own hover state so the list re-renders cheaply. */
const NavRow: React.FC<{
  item: { label: string; screen: AppScreen; icon: string; count?: number };
  active: boolean;
  onPress: () => void;
  colors: any;
  font: any;
}> = ({ item, active, onPress, colors, font }) => {
  const [hover, setHover] = useState(false);
  const tint = active ? colors.accentInk : hover ? colors.ink : colors.ink2;

  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 11,
        height: 36,
        paddingLeft: 12,
        paddingRight: 8,
        marginBottom: 2,
        borderRadius: 9,
        backgroundColor: active ? colors.accentSoft : hover ? colors.paper2 : 'transparent',
        ...(active ? web({ boxShadow: '0 1px 2px rgba(16,24,40,.06)' }) : {}),
        ...web({ transition: 'background-color .14s ease, transform .14s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      {/* Active accent rail */}
      <View
        style={{
          position: 'absolute',
          left: -10,
          top: 8,
          bottom: 8,
          width: 3,
          borderTopRightRadius: 3,
          borderBottomRightRadius: 3,
          backgroundColor: active ? colors.accent : 'transparent',
        }}
      />
      <SkIcon name={item.icon} size={17} color={active ? colors.accent : tint} strokeWidth={active ? 2.2 : 2} />
      <Text
        numberOfLines={1}
        style={{
          flex: 1,
          fontFamily: active ? font.sansSemibold : font.sansMedium,
          fontSize: 13,
          fontWeight: active ? '600' : '500',
          letterSpacing: -0.1,
          color: tint,
        }}
      >
        {item.label}
      </Text>
      {item.count != null && (
        <View
          style={{
            minWidth: 22,
            height: 19,
            paddingHorizontal: 6,
            borderRadius: 999,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: active ? colors.card : colors.paper3,
            borderWidth: 1,
            borderColor: active ? colors.accentLine : colors.line,
          }}
        >
          <Text style={{ fontFamily: font.monoMedium, fontSize: 10.5, color: active ? colors.accentInk : colors.ink3 }}>
            {item.count}
          </Text>
        </View>
      )}
    </Pressable>
  );
};

/** Appearance group header label — matches the nav group label style. */
const GroupLabel: React.FC<{ label: string; colors: any; font: any }> = ({ label, colors, font }) => (
  <Text
    style={{
      fontSize: 10, fontFamily: font.sansSemibold, fontWeight: '600', letterSpacing: 1, textTransform: 'uppercase',
      color: colors.ink4, paddingHorizontal: 12, paddingTop: 4, paddingBottom: 6,
    }}
  >
    {label}
  </Text>
);

/** Dark-mode row with an animated pill switch — lives in the sidebar nav. */
const DarkModeRow: React.FC<{ on: boolean; onToggle: () => void; colors: any; font: any }> = ({ on, onToggle, colors, font }) => {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onToggle}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: 11, height: 36, paddingLeft: 12, paddingRight: 10, borderRadius: 9,
        backgroundColor: hover ? colors.paper2 : 'transparent',
        ...web({ transition: 'background-color .14s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <SkIcon name="moon" size={17} color={on ? colors.accent : hover ? colors.ink : colors.ink2} strokeWidth={2} />
      <Text style={{ flex: 1, fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', letterSpacing: -0.1, color: hover ? colors.ink : colors.ink2 }}>
        Dark mode
      </Text>
      {/* Pill switch — justifyContent flips the knob; cross-platform, no transforms needed on native */}
      <View
        style={{
          width: 42, height: 24, borderRadius: 999, padding: 3, flexDirection: 'row', alignItems: 'center',
          justifyContent: on ? 'flex-end' : 'flex-start',
          backgroundColor: on ? colors.accent : colors.paper3,
          borderWidth: 1, borderColor: on ? colors.transparent : colors.line,
          ...web({ transition: 'background-color .16s ease', backgroundImage: on ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})` : undefined }),
        }}
      >
        <View style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: '#fff', ...web({ boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'all .16s ease' }) }} />
      </View>
    </Pressable>
  );
};

const Sidebar: React.FC<Props> = ({ active, onNavigate }) => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { colors, font } = theme;
  const { exitApp } = useAuthFlow();
  const { org, user, role } = useSession();
  const orgName = org?.name || 'SK Billing';
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '–';
  const fullName = user ? `${user.firstName} ${user.lastName}` : 'Signed in';
  const email = user?.email || (role ? `${role.charAt(0)}${role.slice(1).toLowerCase()}` : 'Signed in');

  const [footerHover, setFooterHover] = useState(false);
  const [logoutHover, setLogoutHover] = useState(false);
  const [orgHover, setOrgHover] = useState(false);

  return (
    <View
      style={{
        width: 252,
        flexShrink: 0,
        backgroundColor: colors.card,
        borderRightWidth: 1,
        borderRightColor: colors.line,
        // Full viewport height on web — a plain '100%' doesn't resolve through
        // the mobile drawer's absolutely-positioned container, which left the
        // footer floating mid-screen. 100vh pins it to the bottom everywhere.
        height: isWeb ? ('100vh' as any) : '100%',
        ...web({ backgroundImage: `linear-gradient(180deg, ${colors.card} 0%, ${colors.paper} 100%)` }),
      }}
    >
      {/* Header */}
      <View
        style={{
          height: 60,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 11,
          paddingHorizontal: 16,
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
        }}
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            backgroundColor: colors.accent,
            alignItems: 'center',
            justifyContent: 'center',
            ...theme.shadow('base'),
            ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accent} 0%, ${colors.accentInk} 100%)` }),
          }}
        >
          <SkIcon name="rupee" size={17} color="#fff" strokeWidth={2.6} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            numberOfLines={1}
            style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 14, color: colors.ink, letterSpacing: -0.3 }}
          >
            SK Billing
          </Text>
          <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3, letterSpacing: -0.1 }}>
            {orgName}
          </Text>
        </View>
        <Pressable
          onPress={() => onNavigate('settings')}
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: orgHover ? colors.paper2 : 'transparent',
            ...web({ transition: 'background-color .14s ease', cursor: 'pointer' }),
          }}
          {...(isWeb ? { onMouseEnter: () => setOrgHover(true), onMouseLeave: () => setOrgHover(false) } : {})}
        >
          <SkIcon name="orgSwitch" size={15} color={orgHover ? colors.ink2 : colors.ink3} strokeWidth={2} />
        </Pressable>
      </View>

      {/* Nav */}
      <ScrollView
        style={{ flex: 1, ...web({ scrollbarWidth: 'thin', scrollbarColor: `${colors.lineStrong} transparent` }) }}
        contentContainerStyle={{ padding: 12, paddingBottom: 20 }}
      >
        {NAV_GROUPS.map((group) => (
          <View key={group.label} style={{ marginBottom: 16 }}>
            <Text
              style={{
                fontSize: 10,
                fontFamily: font.sansSemibold,
                fontWeight: '600',
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: colors.ink4,
                paddingHorizontal: 12,
                paddingTop: 4,
                paddingBottom: 6,
              }}
            >
              {group.label}
            </Text>
            {group.items.map((item) => (
              <NavRow
                key={item.screen}
                item={item}
                active={item.screen === active}
                onPress={() => onNavigate(item.screen)}
                colors={colors}
                font={font}
              />
            ))}
          </View>
        ))}

        {/* Appearance — Dark mode toggle, under the Accountant section */}
        <View style={{ marginBottom: 16 }}>
          <GroupLabel label="Appearance" colors={colors} font={font} />
          <DarkModeRow on={isDark} onToggle={toggleTheme} colors={colors} font={font} />
        </View>
      </ScrollView>

      {/* Footer — premium user bar with gradient avatar + hover profile card */}
      <View style={{ position: 'relative' }}>
        {/* Floating profile card (web hover) — name, email, Log out */}
        {isWeb && (
          <View
            style={{
              position: 'absolute',
              left: 12,
              right: 12,
              bottom: 70,
              padding: 6,
              backgroundColor: colors.card,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: 12,
              ...theme.shadow('lg'),
              ...web({
                opacity: footerHover ? 1 : 0,
                visibility: footerHover ? 'visible' : 'hidden',
                transform: footerHover ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.98)',
                transformOrigin: 'bottom left',
                transition: 'opacity .15s ease, transform .15s ease, visibility .15s',
                pointerEvents: footerHover ? 'auto' : 'none',
              }),
            }}
            {...(isWeb ? { onMouseEnter: () => setFooterHover(true), onMouseLeave: () => setFooterHover(false) } : {})}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, padding: 8, paddingBottom: 9 }}>
              <View
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 19,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#db2777',
                  ...web({ backgroundImage: 'linear-gradient(135deg, #f97316, #db2777)' }),
                }}
              >
                <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 13, color: '#fff' }}>{initials}</Text>
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text numberOfLines={1} style={{ fontFamily: font.sansSemibold, fontWeight: '600', fontSize: 13, color: colors.ink, letterSpacing: 0.1 }}>
                  {fullName}
                </Text>
                <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontWeight: '500', fontSize: 12, color: colors.ink3, marginTop: 2 }}>
                  {email}
                </Text>
              </View>
            </View>
            <View style={{ height: 1, marginHorizontal: 8, backgroundColor: colors.line }} />
            <Pressable
              onPress={exitApp}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 9,
                paddingVertical: 9,
                paddingHorizontal: 10,
                borderRadius: 5,
                backgroundColor: logoutHover ? colors.dangerSoft : 'transparent',
                ...web({ transition: 'background-color .1s ease', cursor: 'pointer' }),
              }}
              {...(isWeb ? { onMouseEnter: () => setLogoutHover(true), onMouseLeave: () => setLogoutHover(false) } : {})}
            >
              <SkIcon name="logout" size={14} color={colors.danger} strokeWidth={2} />
              <Text style={{ fontFamily: font.sansMedium, fontWeight: '500', fontSize: 13, color: colors.danger }}>Log out</Text>
            </Pressable>
          </View>
        )}

        {/* The bar itself */}
        <Pressable
          onPress={() => onNavigate('settings')}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 11,
            borderTopWidth: 1,
            borderTopColor: colors.line,
            paddingVertical: 13,
            paddingHorizontal: 16,
            backgroundColor: footerHover ? colors.paper3 : colors.paper2,
            ...web({
              backgroundImage: footerHover ? undefined : `linear-gradient(180deg, ${colors.paper2}, ${colors.paper3})`,
              transition: 'background-color .1s ease',
              cursor: 'pointer',
            }),
          }}
          {...(isWeb ? { onMouseEnter: () => setFooterHover(true), onMouseLeave: () => setFooterHover(false) } : {})}
        >
          {/* Gradient avatar with ring + glow + presence dot */}
          <View style={{ width: 34, height: 34 }}>
            <View
              style={{
                width: 34,
                height: 34,
                borderRadius: 17,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#db2777',
                ...web({
                  backgroundImage: 'linear-gradient(135deg, #f97316, #db2777)',
                  boxShadow: footerHover
                    ? `0 0 0 2px ${colors.card}, 0 6px 14px -3px rgba(219,39,119,0.5)`
                    : `0 0 0 2px ${colors.card}, 0 3px 8px -2px rgba(219,39,119,0.45)`,
                  transform: footerHover ? 'translateY(-1px)' : 'translateY(0)',
                  transition: 'transform .15s ease, box-shadow .15s ease',
                }),
              }}
            >
              <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 12, color: '#fff' }}>{initials}</Text>
            </View>
            <View
              style={{
                position: 'absolute',
                right: -1,
                bottom: -1,
                width: 10,
                height: 10,
                borderRadius: 5,
                backgroundColor: colors.success,
                borderWidth: 2,
                borderColor: colors.card,
              }}
            />
          </View>

          <View style={{ flex: 1, minWidth: 0 }}>
            <Text numberOfLines={1} style={{ fontFamily: font.sansSemibold, fontWeight: '600', fontSize: 12.5, color: colors.ink, letterSpacing: 0.1 }}>
              {fullName}
            </Text>
            <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontWeight: '500', fontSize: 11, color: colors.ink3, marginTop: 1 }}>
              {email}
            </Text>
          </View>

          {/* No hover card on native or touch screens, so expose Log out inline */}
          {!canHover && (
            <Pressable onPress={exitApp} hitSlop={8} style={{ padding: 6 }}>
              <SkIcon name="logout" size={18} color={colors.ink3} strokeWidth={2} />
            </Pressable>
          )}
        </Pressable>
      </View>
    </View>
  );
};

export default Sidebar;
